import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner } from "tamagui";
import { RiCheckLine, RiErrorWarningLine, RiTimeLine } from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { formatGasTitle } from "@utils/func/gasTitle";
import { appToast } from "@components/Toast";
import { getPayLinkToken, storePayLinkToken, clearPayLinkToken } from "@utils/payLink";
import { useApplePay } from "@hooks/useApplePay";
import ApplePayButton from "@components/payment/ApplePayButton";

import SectionLabel from "../Account/components/ui/SectionLabel";
import { handleAPIGetPayLinkStatus, handleAPICreatePayLinkIntent, type PayLinkStatus } from "../Account/API/handlePaymentAPI";

type View_ = "loading" | "error" | "link_expired" | "paid" | "not_payable" | "payable" | "success";

/**
 * 是否顯示 Apple Pay 不可用的實際原因（`[dev] {reason}`）：本機開發（NODE_ENV=development）
 * 或部署在 dev 環境的站（REACT_APP_SENTRY_ENVIRONMENT=dev）才顯示；pre-prod/prod 不受影響。
 */
const showApReason = process.env.NODE_ENV === "development" || process.env.REACT_APP_SENTRY_ENVIRONMENT === "dev";

/**
 * 後端錯誤訊息不可直接顯示；統一對應中文友善提示。
 * PAY_TOKEN_EXPIRED／PAY_TOKEN_USED 這兩個連結本身失效的狀況要優先判斷（呼叫端會另外切畫面，
 * 這裡的文字只在還是走一般 toast 的情況下用得到），再落到 TapPay 拒絕／prime 過期等一般訊息。
 */
const mapApplePayErr = (error: unknown): string => {
  const raw = typeof error === "string" ? error : "";
  if (/PAY_TOKEN_EXPIRED/.test(raw)) return "連結已過期，請請配送員重新出示付款碼";
  if (/PAY_TOKEN_USED/.test(raw)) return "此訂單已完成付款";
  if (/prime|expired/i.test(raw)) return "付款授權已逾時，請再按一次 Apple Pay";
  if (/already|active payment/i.test(raw)) return "付款處理中，請稍候";
  return "付款失敗，請稍後再試";
};

/**
 * 一次性付款連結模式 /pay/:orderId?pt=<token>（Apple Pay 掃碼／外開 Safari 用）。
 * 整頁不依賴 cusJwtToken／chooseIdentity：LINE 未登入也能開，用 pay_token 帶身分。
 * 只提供 Apple Pay 一種付款方式（本模式的存在意義就是繞過 LINE 登入去用 Apple Pay；
 * 要用信用卡的客戶請回 LINE 走既有付款頁）。
 */
export const PayLinkView = () => {
  const { orderId } = useParams();
  // 凍結掛載當下的 pt：讀完後會 replaceState 把 query 清掉，若每次 render 重讀 window.location.search，
  // 之後任何一次重新渲染都會讀到 pt=null（除非已存入 sessionStorage 備援），導致 effect 重跑並把
  // 已經載入好的畫面覆蓋成 error。
  const [pt] = useState<string | null>(() => getPayLinkToken());

  const [view, setView] = useState<View_>("loading");
  const [errMsg, setErrMsg] = useState("");
  const [status, setStatus] = useState<PayLinkStatus | null>(null);
  const [successInfo, setSuccessInfo] = useState<{ amount: number; label: string; time: string } | null>(null);
  // Apple Pay 按鈕在 intent 呼叫期間鎖住：ap.state 在 pay() 的 finally 就已經回到 "ready"
  // （見 useApplePay.pay），此後到 handleAPICreatePayLinkIntent 回來之前有一段空窗，
  // 連點會打出多筆 intent 請求；用獨立於 ap.state 的 submitting 蓋住整段 handler。
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!pt) {
      setErrMsg("付款連結無效");
      setView("error");
      return;
    }
    // 拿到 pt 就先存進 sessionStorage 備援（key 綁 orderId），讓等一下的 replaceState
    // 清掉網址列後，重新整理或任何重新求值都還讀得到；付款完成／連結失效時清掉。
    if (orderId) storePayLinkToken(orderId, pt);
    let alive = true;
    (async () => {
      const r = await handleAPIGetPayLinkStatus(pt);
      // 讀完就把 pay_token 從網址列移除，避免殘留在瀏覽器歷史 / 分享截圖外流（sessionStorage 已存一份備援）。
      window.history.replaceState(null, "", window.location.pathname);
      if (!alive) return;

      if (r.state !== "success") {
        const raw = typeof r.error === "string" ? r.error : "";
        if (r.status === 401 && /PAY_TOKEN_EXPIRED/.test(raw)) {
          if (orderId) clearPayLinkToken(orderId);
          setView("link_expired");
          return;
        }
        if (r.status === 401 && /PAY_TOKEN_USED/.test(raw)) {
          if (orderId) clearPayLinkToken(orderId);
          setView("paid");
          return;
        }
        setErrMsg("無法載入付款資訊");
        setView("error");
        return;
      }

      setStatus(r.data);
      if (r.data.paid) {
        if (orderId) clearPayLinkToken(orderId);
        setView("paid");
        return;
      }
      if (!r.data.payable) {
        setView("not_payable");
        return;
      }
      setView("payable");
    })();
    return () => {
      alive = false;
    };
  }, [pt, orderId]);

  // Apple Pay 狀態機需固定呼叫（不可依 view 條件式呼叫 hook）；用 enabled 在非 payable 畫面前保持閒置。
  const ap = useApplePay({
    amount: status?.amount ?? null,
    label: status?.pay_link.supplier_name ?? "",
    enabled: view === "payable",
  });

  const onApplePay = async () => {
    if (!pt || !status || submitting) return;
    setSubmitting(true);
    try {
      const r = await ap.pay();
      if (!r.ok) {
        appToast.error(mapApplePayErr(r.msg));
        return;
      }
      const res = await handleAPICreatePayLinkIntent(pt, status.platform_order_id, r.prime);
      if (res.state === "success") {
        if (orderId) clearPayLinkToken(orderId);
        setSuccessInfo({
          amount: status.amount ?? res.data?.amount ?? 0,
          label: "Apple Pay",
          time: new Date().toLocaleString("zh-TW", { hour12: false }),
        });
        setView("success");
        return;
      }
      const raw = typeof res.error === "string" ? res.error : "";
      if (/PAY_TOKEN_EXPIRED/.test(raw)) {
        if (orderId) clearPayLinkToken(orderId);
        setView("link_expired");
        return;
      }
      if (/PAY_TOKEN_USED/.test(raw)) {
        // 已透過此連結完成過付款（例如重覆送出）；視為已付款，直接顯示 paid 畫面
        // （沿用已載入的 status.amount/label，比純載入期的 PAY_TOKEN_USED 分支資訊更完整）。
        if (orderId) clearPayLinkToken(orderId);
        setView("paid");
        return;
      }
      appToast.error(mapApplePayErr(raw));
    } catch {
      appToast.error("付款失敗，請稍後再試");
    } finally {
      setSubmitting(false);
    }
  };

  const headerNode = (title: string, sub?: string) => (
    <Header>
      <Text color={colors.light} fontSize={22} fontWeight="800" mb="$1.5">
        {title}
      </Text>
      {sub ? (
        <Text color="rgba(255,255,255,0.85)" fontSize={12}>
          {sub}
        </Text>
      ) : null}
    </Header>
  );

  if (view === "loading") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <YStack f={1} ai="center" jc="center">
          <Spinner size="large" color={colors.primary} />
        </YStack>
      </YStack>
    );
  }

  if (view === "error") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiErrorWarningLine size={40} color={colors.hint} />
          <Text fontSize={14} color={colors.sub} ta="center">
            {errMsg}
          </Text>
        </YStack>
      </YStack>
    );
  }

  if (view === "link_expired") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiTimeLine size={40} color={colors.hint} />
          <Text fontSize={15} fontWeight="700" color={colors.text} ta="center">
            連結已過期，請請配送員重新出示付款碼
          </Text>
        </YStack>
      </YStack>
    );
  }

  if (view === "not_payable") {
    const st = status?.order_status;
    const hint =
      status?.payment_status === "cash_fallback"
        ? "配送員已改為現金收款，請以現金支付給配送員。"
        : st === "pending" || st === "waiting_for_pickup"
          ? "瓦斯配送中才能線上付款，請稍候再回來這頁。"
          : st === "canceled"
            ? "訂單已取消，無需付款。"
            : st === "completed"
              ? "訂單已完成結案；若對款項有疑問請聯絡瓦斯行。"
              : "若已現場付款，無需再付款；有疑問請聯絡瓦斯行。";
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiErrorWarningLine size={40} color={colors.hint} />
          <Text fontSize={15} fontWeight="700" color={colors.text}>
            這筆訂單尚未開放線上付款
          </Text>
          <Text fontSize={12} color={colors.hint} ta="center">
            {hint}
          </Text>
        </YStack>
      </YStack>
    );
  }

  if (view === "success" || view === "paid") {
    const tx = status?.transaction;
    const amount = view === "success" ? successInfo?.amount ?? 0 : tx?.amount ?? status?.amount ?? 0;
    const label = view === "success" ? successInfo?.label ?? "Apple Pay" : tx?.lastfour ? `信用卡 •••• ${tx.lastfour}` : status?.label ?? "Apple Pay";
    const timeText =
      view === "success" ? successInfo?.time ?? "" : tx?.paid_at ? new Date(tx.paid_at).toLocaleString("zh-TW", { hour12: false }) : "";
    // PAY_TOKEN_USED 錯誤路徑沒有 status（沒打成功過 API），此時無法得知 return_url，退回「可以關閉此頁」文案。
    const returnUrl = status?.pay_link.return_url ?? null;
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <RadiusContentWrapper>
          <YStack ai="center" gap="$3" pt="$6">
            <View w={72} h={72} br={36} bg="#eafaf0" ai="center" jc="center">
              <RiCheckLine size={36} color={colors.green} />
            </View>
            <Text fontSize={17} fontWeight="800" color={colors.text}>
              已付款
            </Text>
            <Text fontSize={12} color={colors.sub} ta="center">
              {view === "success" ? "已通知瓦斯行，訂單狀態同步更新" : timeText ? `${timeText} 已完成付款` : "這筆訂單已完成付款"}
            </Text>
            <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$3" width="100%" gap="$1.5">
              <XStack jc="space-between">
                <Text fontSize={13} color={colors.sub}>
                  付款金額
                </Text>
                <Text fontSize={13} fontWeight="700" color={colors.text} style={{ fontVariantNumeric: "tabular-nums" }}>
                  NT$ {amount.toLocaleString()}
                </Text>
              </XStack>
              <XStack jc="space-between">
                <Text fontSize={13} color={colors.sub}>
                  付款方式
                </Text>
                <Text fontSize={13} fontWeight="700" color={colors.text}>
                  {label}
                </Text>
              </XStack>
              {view === "success" && successInfo?.time ? (
                <XStack jc="space-between">
                  <Text fontSize={13} color={colors.sub}>
                    時間
                  </Text>
                  <Text fontSize={13} fontWeight="700" color={colors.text}>
                    {successInfo.time}
                  </Text>
                </XStack>
              ) : null}
            </YStack>
            {returnUrl ? (
              <a href={returnUrl} style={{ width: "100%", textDecoration: "none" }}>
                <YStack br={12} py="$3.5" ai="center" className="primary-bg-gradient" cursor="pointer" pressStyle={{ opacity: 0.85 }}>
                  <Text fontSize={15} fontWeight="800" color={colors.light}>
                    回到 LINE
                  </Text>
                </YStack>
              </a>
            ) : (
              <Text fontSize={12} color={colors.hint} ta="center">
                可以關閉此頁
              </Text>
            )}
          </YStack>
        </RadiusContentWrapper>
      </YStack>
    );
  }

  // view === "payable"
  if (!status) return null;
  const { pay_link } = status;
  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      {headerNode("付款", `${pay_link.supplier_name} · 訂單 ${pay_link.display_id}`)}
      <RadiusContentWrapper space="$4">
        <YStack>
          <SectionLabel title="訂單摘要" />
          <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$3" gap="$1">
            {pay_link.items.map((it, idx) => {
              const title = it.kind === "gas" ? formatGasTitle(it.gas_type, it.gas_kilogram ?? "") : it.commodity_name ?? "商品";
              const qty = it.kind === "gas" ? it.numbers_of_cylinder : it.numbers_of_commodity;
              return (
                <Text key={idx} fontSize={13} color={colors.sub}>
                  {title} × {qty ?? 1}
                </Text>
              );
            })}
            <XStack jc="space-between" ai="baseline" mt="$2" pt="$2" btw={1} btc={colors.borderLight}>
              <Text fontSize={12} color={colors.sub}>
                應付金額
              </Text>
              <Text fontSize={22} fontWeight="800" color={colors.primary} style={{ fontVariantNumeric: "tabular-nums" }}>
                NT$ {(status.amount ?? 0).toLocaleString()}
              </Text>
            </XStack>
          </YStack>
        </YStack>

        <YStack gap="$2" pb="$6">
          {ap.state === "unavailable" ? (
            <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$5" ai="center" gap="$2">
              <RiErrorWarningLine size={26} color={colors.hint} />
              <Text fontSize={13.5} fontWeight="700" color={colors.text} ta="center">
                此裝置不支援 Apple Pay，請回 LINE 以信用卡付款
              </Text>
              {showApReason && ap.reason ? (
                <Text fontSize={11} color={colors.hint} ta="center">
                  [dev] {ap.reason}
                </Text>
              ) : null}
              {pay_link.return_url ? (
                <a href={pay_link.return_url} style={{ textDecoration: "none" }}>
                  <Text fontSize={13} fontWeight="700" color={colors.primary}>
                    回到 LINE
                  </Text>
                </a>
              ) : null}
            </YStack>
          ) : (
            <ApplePayButton state={submitting ? "paying" : ap.state} onPress={onApplePay} />
          )}
        </YStack>
      </RadiusContentWrapper>
    </YStack>
  );
};

export default PayLinkView;
